import { useCallback } from 'react';
import { ConfigType } from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDateTime } from '../helpers';

export type FormatDateAndTimeByLocation = (
  locationId: string
) => (dateTime: ConfigType, format: string, shouldShowTimezoneAbbr?: boolean) => string;

export const useLocationTimezone = (locationId = '') => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { locationTimezoneMap, userTimezone } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore(
    'locationTimezoneMap',
    'userTimezone'
  );

  const getLocationTimezone = useCallback(
    (locationId: string) => {
      const location = locationTimezoneMap[locationId];
      return isScheduleV3FlagEnabled && location?.isUserOutsideTimezone ? location.timezone || '' : '';
    },
    [isScheduleV3FlagEnabled, locationTimezoneMap]
  );

  const getLocationTimezoneAbbr = useCallback(
    (locationId: string) => {
      const location = locationTimezoneMap[locationId];
      return isScheduleV3FlagEnabled && location?.isUserOutsideTimezone ? location.timezoneAbbr || '' : '';
    },
    [isScheduleV3FlagEnabled, locationTimezoneMap]
  );

  const formatDateAndTimeByLocation: FormatDateAndTimeByLocation = useCallback(
    (locationId) =>
      (dateTime, format, shouldShowTimezoneAbbr = false) => {
        const {
          timezone: locationTimezone,
          timezoneAbbr,
          isUserOutsideTimezone,
        } = locationTimezoneMap[locationId] || {};

        const formattedDateTime = formatDateTime(dateTime, {
          fromTimezone: 'UTC',
          toTimezone: isScheduleV3FlagEnabled ? locationTimezone : userTimezone,
          outputFormat: format,
        });

        // Show timezone abbreviation only if the user is outside the location's timezone and v3 is enabled
        return isScheduleV3FlagEnabled && isUserOutsideTimezone && shouldShowTimezoneAbbr
          ? `${formattedDateTime} ${timezoneAbbr}`.trim()
          : formattedDateTime;
      },
    [isScheduleV3FlagEnabled, locationTimezoneMap, userTimezone]
  );

  const formatDateAndTime = formatDateAndTimeByLocation(locationId);

  const locationTimezone = locationTimezoneMap[locationId]?.timezone;
  const locationTimezoneAbbr = getLocationTimezoneAbbr(locationId);

  return {
    locationTimezoneAbbr,
    locationTimezone,
    formatDateAndTime,
    formatDateAndTimeByLocation,
    getLocationTimezoneAbbr,
    getLocationTimezone,
  };
};
